/**
 * Element construction.
 *
 * One rule, enforced by shape rather than by review: **dynamic values never
 * become markup.** Text goes through `textContent` and attributes through
 * `setAttribute`, both of which escape by definition. The only field that
 * accepts markup is `staticHtml`, which exists solely so the icon helper can
 * hand over SVG assembled from module-owned constants.
 *
 * A payment interface that concatenates strings into `innerHTML` has an
 * injection surface no amount of downstream sanitising reliably closes. Removing
 * the surface is cheaper and holds under refactoring.
 */

/**
 * Attribute values.
 *
 * `null` and `undefined` always omit the attribute. `false` omits it too — for
 * boolean HTML attributes like `disabled` and `hidden`, where presence *is* the
 * value — **except** on `aria-*`, where state is tri-state: `aria-checked=false`
 * means "unchecked" and its absence means "not a checkbox at all". Collapsing
 * those two produced a real, screen-reader-visible defect caught by
 * `frontend/test/a11y.test.ts`, so the distinction lives in the factory rather
 * than in every caller's memory.
 */
export type AttrValue = string | number | boolean | null | undefined;

export interface ElementSpec {
  /** Space-separated class names. */
  readonly class?: string | undefined;
  /** Text content. Escaped by the platform. */
  readonly text?: string | undefined;
  /**
   * Markup, assigned directly.
   *
   * **Static, module-owned strings only.** Never a value derived from input,
   * configuration, a URL, or anything a bank or a person supplied.
   */
  readonly staticHtml?: string | undefined;
  readonly attrs?: Readonly<Record<string, AttrValue>> | undefined;
  /** `data-*` attributes, without the prefix. */
  readonly data?: Readonly<Record<string, string | undefined>> | undefined;
}

export type Child = Node | string | null | undefined | false;

/** Create an element, apply its spec, and append its children. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  spec: ElementSpec = {},
  children: readonly Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  if (spec.class !== undefined) node.className = spec.class;
  if (spec.staticHtml !== undefined) node.innerHTML = spec.staticHtml;
  if (spec.text !== undefined) node.textContent = spec.text;

  if (spec.attrs !== undefined) {
    for (const [name, value] of Object.entries(spec.attrs)) {
      if (value === undefined || value === null) continue;
      if (name.startsWith("aria-")) {
        node.setAttribute(name, String(value));
        continue;
      }
      if (value === false) continue;
      node.setAttribute(name, value === true ? "" : String(value));
    }
  }

  if (spec.data !== undefined) {
    for (const [name, value] of Object.entries(spec.data)) {
      if (value === undefined) continue;
      node.setAttribute(`data-${name}`, value);
    }
  }

  append(node, children);
  return node;
}

/** Append children, skipping the falsy slots that conditional markup produces. */
export function append(parent: Node, children: readonly Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
}

/** Replace an element's children in one operation. */
export function replaceChildren(parent: Element, children: readonly Child[]): void {
  parent.replaceChildren();
  append(parent, children);
}

/**
 * Stable unique ids for label/control/description wiring.
 *
 * A monotonic counter, not randomness. The `prohibited-api` gate forbids
 * `Math.random` in production source and it would be the wrong tool regardless:
 * these ids are not secrets, and a deterministic sequence makes rendered markup
 * reproducible in tests and snapshots.
 */
let idCounter = 0;

export function uniqueId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}

/** Reset the id sequence. Test support only. */
export function resetIdCounter(): void {
  idCounter = 0;
}
